import React from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import * as customColors from "../../../../../theme/customColors";
import { numberWithCommas } from "../../../../../services/utils/utils";
import {
  HaklaiStyledCell as StyledCell,
  HaklaiStyledNameCell as StyledNameCell,
  joinClassNames,
  buildFieldErrorId,
  FieldShell
} from "../../shared/table/haklaiTableShared";
import {
  BUILT_VALUE_PLACEHOLDER,
  SELECT_NAME_PLACEHOLDER,
  SALE_VALUE_PLACEHOLDER
} from "../haklaiPropertyPartsStyles";
import {
  CATEGORIES,
  COMMERCIAL_OPTIONS,
  LAND_AND_SHARED_OPTIONS
} from "../../shared/haklaiConstants";
import { isHaklaiBuiltValueRelevant } from "../../shared/haklaiFlowHelpers";
import {
  HAKLAI_DEPRECIATION_LABELS,
  HAKLAI_DEPRECIATION_TOOLTIPS
} from "../../shared/haklaiDepreciationTexts";
import {
  HAKLAI_PROPERTY_PART_LABELS,
  HAKLAI_PROPERTY_PART_TOOLTIPS
} from "../haklaiPropertyPartsTexts";

// Resolves the color for the "remaining to allocate" cell.
// - Red when the user has over-allocated (remaining < 0).
// - On the final filled row: green when matched, orange when still open.
// - Earlier running totals stay neutral so the final balance remains the cue.
const getRemainingColor = (remaining, isLastFilledRow) => {
  if (remaining < 0) return customColors.red;
  if (!isLastFilledRow) return customColors.darkGray;
  if (remaining === 0) return customColors.green;
  return customColors.orange;
};

const formatCurrencyILS = (value, { empty = "—", decimals = 0 } = {}) => {
  if (value === null || value === undefined || value === "") return empty;
  const num = Number(value);
  if (Number.isNaN(num)) return empty;
  return new Intl.NumberFormat("he-IL", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num);
};

const getColumnGroupFrameClassName = (classes, {
  top = false,
  bottom = false,
  start = false,
  innerStart = false,
  end = false
} = {}) => joinClassNames(
  classes.columnGroupCell,
  top ? classes.columnGroupTop : "",
  bottom ? classes.columnGroupBottom : "",
  start ? classes.columnGroupStart : "",
  innerStart ? classes.columnGroupInnerStart : "",
  end ? classes.columnGroupEnd : ""
);

const renderMutedCellValue = (classes, tooltipTitle, label = "—") => (
  <Tooltip title={tooltipTitle}>
    <Typography component="span" className={classes.nonRelevantValue}>
      {label}
    </Typography>
  </Tooltip>
);

const renderQuietFieldPlaceholder = (classes, shellClassName = "", label = "—", showCurrency = false) => (
  <FieldShell
    classes={classes}
    shellClassName={shellClassName}
    showCurrency={showCurrency}
    currencyHidden={showCurrency}
  >
    <Box className={classes.fieldShellPlaceholder}>
      <Typography component="span" className={classes.nonRelevantValue}>
        {label}
      </Typography>
    </Box>
  </FieldShell>
);

// The selected value of this dropdown is the part's `assetTypeKey` — the
// canonical, language-stable identity from the option list. The Hebrew
// `label` is written alongside as a snapshot so reports / read-only views
// keep working without needing to look up the option list. Any stored
// assetTypeKey that no longer matches an option (e.g. the option list was
// pruned in a later release) is preserved as a disabled "legacy" entry so
// the user does not silently lose data.
const HaklaiSelectNameField = ({
  classes,
  rowKey,
  options,
  assetTypeKey,
  fallbackLabel,
  displayLabel,
  nameFieldState,
  errorMsg,
  onChange,
  onBlur
}) => {
  const hasLabelError = Boolean(errorMsg) || Boolean(nameFieldState?.hasError);
  const matchedOption = options.find((opt) => opt.key === assetTypeKey);
  const legacyOption = !matchedOption && assetTypeKey
    ? { key: assetTypeKey, label: fallbackLabel || assetTypeKey, isLegacy: true }
    : null;
  const resolvedOptions = legacyOption ? [legacyOption, ...options] : options;
  const selectedKey = matchedOption?.key || legacyOption?.key || "";
  const fieldClassName = [
    classes.commercialNameInput,
    nameFieldState?.isOptionalTrailing ? classes.optionalTrailingField : ""
  ]
    .filter(Boolean)
    .join(" ");
  const errorId = buildFieldErrorId(rowKey, "label");

  return (
    <FieldShell
      classes={classes}
      shellClassName={classes.nameFieldShell}
      errorMsg={errorMsg}
      errorId={errorId}
    >
      <TextField
        select
        name={`${rowKey}_assetType`}
        variant="outlined"
        size="small"
        className={fieldClassName}
        value={selectedKey}
        onChange={(event) => {
          const selected = resolvedOptions.find((opt) => opt.key === event.target.value);
          // Write both fields atomically (two queued setCalc reductions) so
          // `assetTypeKey` and the `label` snapshot never drift apart.
          onChange(rowKey, "assetTypeKey", selected?.key || "");
          onChange(rowKey, "label", selected?.label || "");
        }}
        onBlur={() => onBlur(rowKey, "label")}
        error={hasLabelError}
        inputProps={{
          "aria-invalid": hasLabelError,
          "aria-describedby": hasLabelError && errorMsg ? errorId : undefined
        }}
        SelectProps={{
          displayEmpty: true,
          SelectDisplayProps: {
            "data-haklai-name-select": rowKey,
            "aria-invalid": hasLabelError,
            "aria-describedby": hasLabelError && errorMsg ? errorId : undefined
          },
          renderValue: (selected) => {
            if (!selected) {
              return (
                <span style={{ color: "#9e9e9e", fontWeight: 400 }}>
                  {SELECT_NAME_PLACEHOLDER}
                </span>
              );
            }
            // Prefer the auto-numbered display label so the user can tell
            // duplicate types apart inline with the field they just picked.
            if (displayLabel) return displayLabel;
            return resolvedOptions.find((opt) => opt.key === selected)?.label || selected;
          }
        }}
      >
        {resolvedOptions.map((option) => (
          <MenuItem key={option.key} value={option.key} disabled={option.isLegacy === true}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    </FieldShell>
  );
};

export const HaklaiPropertyPartRow = React.memo(({
  classes,
  rowKey,
  part,
  hasZaban,
  ptorType,
  hasTzinunOnPtor,
  lastResidentialKey,
  subSectionLabel,
  subSectionRowSpan,
  isFirstInSubSection,
  rowValidationState,
  nameFieldState,
  displayLabel,
  showBuiltValueColumn,
  showZabanColumn,
  showPre1997ConstructionQuestion,
  remainingAfterRow,
  isLastFilledRow,
  isFirstInBody = false,
  isLastInBody = false,
  depreciationState = {},
  onOpenDepreciation,
  onRentalDepreciationToggle,
  onFieldChange,
  onFieldBlur,
  onRemove,
  setValueInputRef,
  setBuiltValueInputRef,
  setRowRef,
  cellErrors = {},
  isFlashing = false
}) => {
  const isResidential = part.category === CATEGORIES.RESIDENTIAL;
  const isCommercial = part.category === CATEGORIES.COMMERCIAL;
  const isCommercialBg = part.category === CATEGORIES.COMMERCIAL_BG;
  const isLandShared = part.category === CATEGORIES.LAND_AND_SHARED;
  const isEditableName = isCommercial || isCommercialBg || isLandShared;
  const isBuiltValueRelevant = isHaklaiBuiltValueRelevant(part, hasZaban);
  const showRentalDepreciationControl = depreciationState.isResidentialAutoAllowed === true;
  const isRentalDepreciationChecked = (
    depreciationState.hasRentalSelected === true
    || depreciationState.hasRentalConfigured === true
  );
  const purchaseDepreciationError = cellErrors.purchaseDepreciation || "";
  const purchaseDepreciationErrorId = buildFieldErrorId(rowKey, "purchaseDepreciation");
  const hasPurchaseDepreciationError = Boolean(purchaseDepreciationError);
  const rentalDepreciationDisabled = !depreciationState.isRelevant;
  const rentalEditDisabled = rentalDepreciationDisabled || !isRentalDepreciationChecked;
  const rentalQuestionTooltip = rentalDepreciationDisabled
    ? HAKLAI_DEPRECIATION_TOOLTIPS.rentalNotRelevant
    : isRentalDepreciationChecked
      ? HAKLAI_DEPRECIATION_TOOLTIPS.removeRentalDepreciation
      : HAKLAI_DEPRECIATION_TOOLTIPS.enterRentalDepreciation;
  const rentalEditTooltip = rentalEditDisabled
    ? HAKLAI_DEPRECIATION_TOOLTIPS.mustMarkRentedBeforeEdit
    : depreciationState.hasInvalid
      ? purchaseDepreciationError
        || depreciationState.validationError
        || HAKLAI_DEPRECIATION_TOOLTIPS.rentalDepreciationNeedsReview
      : HAKLAI_DEPRECIATION_TOOLTIPS.editRentalDepreciation;
  const partSaleValue = Number(part.value) || 0;
  const hasSaleValue = part.value !== null && part.value !== undefined && part.value !== "";
  const hasBuiltValueRaw =
    part.valueWithoutZaban !== null
    && part.valueWithoutZaban !== undefined
    && part.valueWithoutZaban !== "";
  const builtValueNumber = hasBuiltValueRaw ? Number(part.valueWithoutZaban) : null;
  const hasPositiveBuiltValue = Number.isFinite(builtValueNumber) && builtValueNumber > 0;
  const zabanValue = (
    isResidential
    && hasSaleValue
    && hasPositiveBuiltValue
  ) ? Number(partSaleValue) - Number(builtValueNumber) : null;
  // All per-cell error surfaces are driven from the overlay's `cellErrors`.
  // The only exception is the optional part-name field, which uses a local
  // `nameFieldState` because it combines a required check with a uniqueness
  // warning that lives outside the overlay.
  const hasLabelError = Boolean(cellErrors.label) || Boolean(nameFieldState?.hasError);
  const hasValueError = Boolean(cellErrors.value);
  const hasBuiltValueError = Boolean(cellErrors.valueWithoutZaban);
  const exemptionMetaLabel = (
    rowKey === "residence_1"
    && ptorType !== "none"
    && !(ptorType === "single" && hasTzinunOnPtor)
  ) ? (
    ptorType === "single"
      ? HAKLAI_PROPERTY_PART_LABELS.singleApartmentExemption
      : HAKLAI_PROPERTY_PART_LABELS.inheritanceExemption
  ) : "";
  const exemptionBadgeLabel = exemptionMetaLabel
    ? ptorType === "single"
      ? HAKLAI_PROPERTY_PART_LABELS.singleApartmentExemptionBadge
      : HAKLAI_PROPERTY_PART_LABELS.inheritanceExemptionBadge
    : "";

  return (
    <TableRow
      key={rowKey}
      ref={setRowRef ? setRowRef(rowKey) : undefined}
      className={isFlashing ? classes.rowFlash : undefined}
    >
      {isFirstInSubSection ? (
        <StyledCell
          rowSpan={subSectionRowSpan}
          align="center"
          className={joinClassNames(
            classes.subSectionCell,
            isFirstInBody ? classes.propertyLeadingColumnTopRule : ""
          )}
        >
          <Box className={classes.subSectionCellInner}>
            <Typography variant="body2" className={classes.subSectionCellText}>
              {subSectionLabel}
            </Typography>
          </Box>
        </StyledCell>
      ) : null}

      <StyledNameCell
        align="right"
        className={isFirstInBody ? classes.propertyLeadingColumnTopRule : undefined}
      >
        <Box
          className={[
            classes.nameFieldStack,
            classes.nameFieldStackCentered
          ].filter(Boolean).join(" ")}
        >
          {isEditableName ? (
            <HaklaiSelectNameField
              classes={classes}
              rowKey={rowKey}
              options={(isCommercial || isCommercialBg) ? COMMERCIAL_OPTIONS : LAND_AND_SHARED_OPTIONS}
              assetTypeKey={part.assetTypeKey || ""}
              fallbackLabel={part.label || ""}
              displayLabel={displayLabel}
              nameFieldState={nameFieldState}
              errorMsg={cellErrors.label || ""}
              onChange={onFieldChange}
              onBlur={onFieldBlur}
            />
          ) : (
            <FieldShell
              classes={classes}
              shellClassName={classes.nameFieldShell}
              errorMsg={cellErrors.label || ""}
              errorId={buildFieldErrorId(rowKey, "label")}
            >
              <TextField
                name={`${rowKey}_label_readonly`}
                variant="standard"
                size="small"
                className={[
                  classes.readOnlyNameInput,
                  exemptionMetaLabel ? classes.readOnlyNameInputWithBadge : "",
                  hasLabelError ? classes.readOnlyNameInputError : ""
                ].filter(Boolean).join(" ")}
                value={part.label || ""}
                InputProps={{
                  readOnly: true,
                  disableUnderline: true,
                  style: { fontSize: "0.875rem", fontWeight: 500 },
                  endAdornment: exemptionMetaLabel ? (
                    <InputAdornment position="end" className={classes.readOnlyNameBadgeAdornment}>
                      <Tooltip title={exemptionMetaLabel}>
                        <span className={classes.readOnlyNameBadge} aria-label={exemptionMetaLabel}>
                          {exemptionBadgeLabel}
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ) : null
                }}
                inputProps={{
                  "aria-invalid": hasLabelError,
                  "aria-describedby": hasLabelError && cellErrors.label
                    ? buildFieldErrorId(rowKey, "label")
                    : undefined
                }}
              />
            </FieldShell>
          )}
        </Box>
      </StyledNameCell>

      <StyledCell
        align="center"
        className={getColumnGroupFrameClassName(classes, {
          top: isFirstInBody,
          bottom: isLastInBody,
          start: true
        })}
      >
        <FieldShell
          classes={classes}
          shellClassName={classes.moneyFieldShell}
          errorMsg={cellErrors.value || ""}
          errorId={buildFieldErrorId(rowKey, "value")}
          showCurrency
        >
          <TextField
            name={`${rowKey}_value`}
            type="text"
            variant="outlined"
            size="small"
            className={[
              classes.numberInput,
              rowValidationState.isVisuallyDrafting ? classes.optionalTrailingField : ""
            ].filter(Boolean).join(" ")}
            value={numberWithCommas(part.value)}
            placeholder={SALE_VALUE_PLACEHOLDER}
            inputRef={setValueInputRef(rowKey)}
            onChange={(event) => onFieldChange(rowKey, "value", event.target.value)}
            onBlur={() => onFieldBlur(rowKey, "value")}
            error={hasValueError}
            inputProps={{
              "aria-invalid": hasValueError,
              "aria-describedby": hasValueError && cellErrors.value
                ? buildFieldErrorId(rowKey, "value")
                : undefined
            }}
          />
        </FieldShell>
      </StyledCell>

      <StyledCell
        align="center"
        className={joinClassNames(
          classes.percentCell,
          getColumnGroupFrameClassName(classes, {
            top: isFirstInBody,
            bottom: isLastInBody,
            innerStart: true
          })
        )}
      >
        {remainingAfterRow !== null && remainingAfterRow !== undefined ? (
          <FieldShell classes={classes} shellClassName={classes.readOnlyMoneyFieldShell} showCurrency>
            <TextField
              name={`${rowKey}_remainingAfterRow`}
              type="text"
              variant="outlined"
              size="small"
              className={classes.readOnlyField}
              value={formatCurrencyILS(remainingAfterRow)}
              InputProps={{
                readOnly: true,
                inputProps: {
                  style: {
                    color: getRemainingColor(remainingAfterRow, isLastFilledRow),
                    fontWeight: isLastFilledRow ? 700 : 600,
                    fontSize: "0.82rem"
                  }
                }
              }}
            />
          </FieldShell>
        ) : (
          renderQuietFieldPlaceholder(classes, classes.readOnlyMoneyFieldShell, "—", true)
        )}
      </StyledCell>

      {showBuiltValueColumn ? (
        <StyledCell
          align="center"
          className={joinClassNames(
            !isResidential || !isBuiltValueRelevant ? classes.nonRelevantCell : "",
            getColumnGroupFrameClassName(classes, {
              top: isFirstInBody,
              bottom: isLastInBody,
              start: true,
              end: !showZabanColumn
            })
          )}
        >
          {!isResidential ? (
            renderMutedCellValue(classes, HAKLAI_PROPERTY_PART_LABELS.notRelevantInSection)
          ) : isBuiltValueRelevant ? (
            <FieldShell
              classes={classes}
              shellClassName={classes.moneyFieldShell}
              errorMsg={cellErrors.valueWithoutZaban || ""}
              errorId={buildFieldErrorId(rowKey, "valueWithoutZaban")}
              showCurrency
            >
              <TextField
                name={`${rowKey}_valueWithoutZaban`}
                type="text"
                variant="outlined"
                size="small"
                className={[
                  classes.numberInput,
                  rowValidationState.isVisuallyDrafting ? classes.optionalTrailingField : ""
                ].filter(Boolean).join(" ")}
                value={numberWithCommas(part.valueWithoutZaban)}
                placeholder={BUILT_VALUE_PLACEHOLDER}
                inputRef={setBuiltValueInputRef(rowKey)}
                onChange={(event) => onFieldChange(rowKey, "valueWithoutZaban", event.target.value)}
                onBlur={() => onFieldBlur(rowKey, "valueWithoutZaban")}
                error={hasBuiltValueError}
                inputProps={{
                  "aria-invalid": hasBuiltValueError,
                  "aria-describedby": hasBuiltValueError && cellErrors.valueWithoutZaban
                    ? buildFieldErrorId(rowKey, "valueWithoutZaban")
                    : undefined
                }}
              />
            </FieldShell>
          ) : (
            renderMutedCellValue(classes, HAKLAI_PROPERTY_PART_LABELS.notRelevantWithoutZaban)
          )}
        </StyledCell>
      ) : null}

      {showZabanColumn ? (
        <StyledCell
          align="center"
          className={joinClassNames(
            !isResidential ? classes.nonRelevantCell : "",
            getColumnGroupFrameClassName(classes, {
              top: isFirstInBody,
              bottom: isLastInBody,
              start: !showBuiltValueColumn,
              innerStart: showBuiltValueColumn,
              end: true
            })
          )}
        >
          {isResidential ? (
            !hasZaban ? (
              renderMutedCellValue(classes, HAKLAI_PROPERTY_PART_LABELS.zabanNotSelected)
            ) : zabanValue !== null ? (
              <Box className={classes.zabanDetailsStack}>
                <FieldShell classes={classes} shellClassName={classes.readOnlyMoneyFieldShell} showCurrency>
                  <TextField
                    name={`${rowKey}_zabanValue`}
                    type="text"
                    variant="outlined"
                    size="small"
                    className={classes.readOnlyFieldWide}
                    value={formatCurrencyILS(zabanValue)}
                    InputProps={{
                      readOnly: true,
                      inputProps: zabanValue < 0
                        ? { style: { color: customColors.red } }
                        : undefined
                    }}
                  />
                </FieldShell>
                {showPre1997ConstructionQuestion ? (
                  <Box className={classes.pre1997QuestionWrap}>
                    <Checkbox
                      color="primary"
                      size="small"
                      checked={part.constructionsDoneBeforeApril1997 === true}
                      onChange={(event) => onFieldChange(
                        rowKey,
                        "constructionsDoneBeforeApril1997",
                        event.target.checked
                      )}
                      className={classes.pre1997Checkbox}
                      inputProps={{
                        "aria-label": HAKLAI_PROPERTY_PART_LABELS.pre1997ConstructionQuestion
                      }}
                    />
                    <Typography component="span" className={classes.pre1997QuestionLabel}>
                      {HAKLAI_PROPERTY_PART_LABELS.pre1997ConstructionQuestion}
                    </Typography>
                  </Box>
                ) : null}
              </Box>
            ) : (
              renderQuietFieldPlaceholder(classes, classes.readOnlyMoneyFieldShell, "—", true)
            )
          ) : (
            renderMutedCellValue(classes, HAKLAI_PROPERTY_PART_LABELS.notRelevantInSection)
          )}
        </StyledCell>
      ) : null}

      <StyledCell
        align="center"
        className={getColumnGroupFrameClassName(classes, {
          top: isFirstInBody,
          bottom: isLastInBody,
          start: !showBuiltValueColumn && !showZabanColumn,
          end: true
        })}
      >
        <Box className={classes.depreciationFieldContent}>
          {showRentalDepreciationControl ? (
            <FieldShell
              classes={classes}
              shellClassName={classes.rentalDepreciationFieldShell}
              errorMsg={purchaseDepreciationError}
              errorId={purchaseDepreciationErrorId}
            >
              <Box className={classes.rentalDepreciationControl}>
                <Box className={classes.rentalDepreciationActionRow}>
                  <Tooltip title={rentalQuestionTooltip}>
                    <span>
                      <RadioGroup
                        row
                        name={`${rowKey}_rentalDepreciationEnabled`}
                        className={classes.rentalDepreciationRadioGroup}
                        value={isRentalDepreciationChecked ? "yes" : "no"}
                        aria-label={HAKLAI_DEPRECIATION_LABELS.rentedQuestion}
                        aria-invalid={hasPurchaseDepreciationError || undefined}
                        aria-describedby={hasPurchaseDepreciationError
                          ? purchaseDepreciationErrorId
                          : undefined}
                        onChange={(event) => onRentalDepreciationToggle?.(
                          rowKey,
                          part,
                          event.target.value === "yes",
                          depreciationState
                        )}
                      >
                        <FormControlLabel
                          value="no"
                          disabled={rentalDepreciationDisabled}
                          className={classes.rentalDepreciationRadioOption}
                          control={<Radio size="small" color="primary" className={classes.rentalDepreciationRadio} />}
                          label={HAKLAI_DEPRECIATION_LABELS.no}
                        />
                        <FormControlLabel
                          value="yes"
                          disabled={rentalDepreciationDisabled}
                          className={classes.rentalDepreciationRadioOption}
                          control={<Radio size="small" color="primary" className={classes.rentalDepreciationRadio} />}
                          label={HAKLAI_DEPRECIATION_LABELS.yes}
                        />
                      </RadioGroup>
                    </span>
                  </Tooltip>

                  <Tooltip title={rentalEditTooltip}>
                    <span>
                      <IconButton
                        size="small"
                        name={`${rowKey}_purchaseDepreciation`}
                        data-field={`${rowKey}_purchaseDepreciation`}
                        disabled={rentalEditDisabled}
                        aria-label={HAKLAI_DEPRECIATION_TOOLTIPS.editRentalDepreciation}
                        className={[
                          classes.rentalDepreciationEditButton,
                          isRentalDepreciationChecked ? classes.rentalDepreciationEditButtonActive : "",
                          depreciationState.hasInvalid ? classes.rentalDepreciationEditButtonWarning : "",
                          rentalEditDisabled ? classes.rentalDepreciationEditButtonDisabled : ""
                        ].filter(Boolean).join(" ")}
                        onClick={() => {
                          if (rentalEditDisabled) return;
                          onOpenDepreciation?.(rowKey, part, depreciationState);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </FieldShell>
          ) : (
            renderQuietFieldPlaceholder(classes)
          )}
        </Box>
      </StyledCell>

      <StyledCell
        align="center"
        style={{ padding: 2 }}
        className={classes.groupFrameTrailingActionCell}
      >
        {rowKey === "residence_1" || (isResidential && rowKey !== lastResidentialKey) ? null : (
          <IconButton
            size="small"
            aria-label={HAKLAI_PROPERTY_PART_TOOLTIPS.deletePart}
            onClick={() => onRemove(rowKey)}
          >
            <DeleteOutlineIcon fontSize="small" style={{ color: customColors.red }} />
          </IconButton>
        )}
      </StyledCell>
    </TableRow>
  );
});
